// PrivacySection.tsx — Privacy & Security. Every control here is enforced
// server-side (see backend/apps/accounts/tests/test_privacy_controls.py):
// no decorative toggles.
import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { Panel, Switch, Button, Badge, Avatar, useToast } from '@/ui';
import { useSession, useFollowRequestsList, useConversationRefresh } from '@/hooks';
import type { SuggestedProfile } from '@/core/models';
import type { VideoChannel } from '@/core/models/post';

const CHANNEL_TOAST: Record<VideoChannel, string> = {
  inherit: 'Your videos now follow your account setting',
  public: 'Your videos are now public by default',
  followers: 'Your videos are now for followers by default',
};
import { authService } from '@/core/services/authService';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { ConfirmDropFollowersDialog } from './ConfirmDropFollowersDialog';
import { TwoFactorEnableDialog, TwoFactorDisableDialog } from '../TwoFactorDialog';

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const Card = styled(Panel)`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const CardTitle = styled.h3`
  margin: 0;
  font-family: ${({ theme }) => theme.font.display};
  font-size: ${({ theme }) => theme.fontSize.md};
  color: ${({ theme }) => theme.color.textPrimary};
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const RowText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const RowLabel = styled.span`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textPrimary};
`;

const RowHint = styled.span`
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
  max-width: 44ch;
`;

const Select = styled.select`
  padding: 6px 10px;
  border-radius: ${({ theme }) => theme.radius.md};
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  background: ${({ theme }) => theme.color.surface};
  color: ${({ theme }) => theme.color.textPrimary};
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
`;

const ManageList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
`;

const ManageRow = styled.li`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 0;
  border-bottom: 1px solid ${({ theme }) => theme.color.glassBorder};

  &:last-child {
    border-bottom: none;
  }
`;

const ManageRowText = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const ManageName = styled.span`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textPrimary};
`;

const ManageHandle = styled.span`
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
`;

const RowActions = styled.div`
  flex: none;
  display: flex;
  gap: 6px;
`;

const StateText = styled.p`
  margin: 0;
  padding: 4px 0;
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
`;

const ErrorRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const RetryButton = styled.button`
  padding: 4px 10px;
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  border-radius: ${({ theme }) => theme.radius.sm};
  background: transparent;
  color: ${({ theme }) => theme.color.textPrimary};
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.color.glassBorder};
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
    outline-offset: 2px;
  }
`;

/**
 * Follow requests — manage list for a private account. Accept/decline reuse
 * followsService (via useFollowRequestsList), the same calls the notification
 * row's inline Accept/Decline make, so followStore stays the single source of
 * truth. Only rendered when the account is private (a public account cannot
 * have pending requests — see api_users.py's `follow` 3-state toggle).
 */
function FollowRequestsCard() {
  const { requests, loading, error, pending, accept, decline, refresh } = useFollowRequestsList();
  const toast = useToast();
  const titleRef = useRef<HTMLHeadingElement>(null);
  const [confirmDeclineRow, setConfirmDeclineRow] = useState<SuggestedProfile | null>(null);

  async function handleAccept(row: SuggestedProfile) {
    const outcome = await accept(row.handle);
    if (outcome === 'busy') return;
    if (outcome === 'accepted') toast(`Accepted ${row.handle}`);
    else if (outcome === 'stale') toast('That request was already handled');
    else toast('Something went wrong. That row is still pending');
  }

  async function runDecline(row: SuggestedProfile) {
    setConfirmDeclineRow(null);
    const outcome = await decline(row.handle);
    if (outcome === 'busy') return;
    if (outcome === 'declined') toast(`Declined ${row.handle}`);
    else if (outcome === 'stale') toast('That request was already handled');
    else toast('Something went wrong. That row is still pending');
  }

  return (
    <Card>
      <CardTitle ref={titleRef} tabIndex={-1}>
        Follow requests
      </CardTitle>
      <RowHint>
        People who asked to follow your private account. Accepting makes them a follower; declining
        removes the request. They can send another one later.
      </RowHint>

      {loading && (
        <StateText role="status" aria-live="polite">
          Loading…
        </StateText>
      )}
      {!loading && error && (
        <ErrorRow role="status" aria-live="polite">
          <StateText as="span">Could not load your follow requests.</StateText>
          <RetryButton type="button" onClick={refresh}>
            Try again
          </RetryButton>
        </ErrorRow>
      )}
      {!loading && !error && requests.length === 0 && (
        <StateText role="status" aria-live="polite">
          No pending follow requests.
        </StateText>
      )}
      {!loading && !error && requests.length > 0 && (
        <ManageList aria-label="Follow requests">
          {requests.map((row) => (
            <ManageRow key={row.handle}>
              <Avatar name={row.name} avatarKey={row.av} size={28} />
              <ManageRowText>
                <ManageName>{row.name}</ManageName>
                <ManageHandle>{row.handle}</ManageHandle>
              </ManageRowText>
              <RowActions>
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={pending.has(row.handle)}
                  onClick={() => setConfirmDeclineRow(row)}
                  aria-label={`Decline follow request from ${row.handle}`}
                >
                  Decline
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  disabled={pending.has(row.handle)}
                  onClick={() => void handleAccept(row)}
                  aria-label={`Accept follow request from ${row.handle}`}
                >
                  Accept
                </Button>
              </RowActions>
            </ManageRow>
          ))}
        </ManageList>
      )}
      {confirmDeclineRow && (
        <ConfirmDialog
          title="Decline follow request?"
          message={`Decline ${confirmDeclineRow.handle}'s follow request? They won't be told, and they can request again.`}
          confirmLabel="Decline"
          onConfirm={() => void runDecline(confirmDeclineRow)}
          onClose={() => setConfirmDeclineRow(null)}
        />
      )}
    </Card>
  );
}

type Audience = 'everyone' | 'following' | 'nobody';

const AUDIENCE_LABELS: Record<Audience, string> = {
  everyone: 'Everyone',
  following: 'People I follow',
  nobody: 'Nobody',
};

function AudienceSelect({
  value,
  label,
  onChange,
}: {
  value: Audience;
  label: string;
  onChange: (next: Audience) => void;
}) {
  return (
    <Select aria-label={label} value={value} onChange={(e) => onChange(e.target.value as Audience)}>
      {(Object.keys(AUDIENCE_LABELS) as Audience[]).map((a) => (
        <option key={a} value={a}>
          {AUDIENCE_LABELS[a]}
        </option>
      ))}
    </Select>
  );
}

export function PrivacySection() {
  const { currentUser, updateProfile, signOutOtherSessions } = useSession();
  const toast = useToast();
  const refreshConversations = useConversationRefresh();
  const [clearing, setClearing] = useState(false);
  const [confirmDropFollowers, setConfirmDropFollowers] = useState(false);
  const [twoFactorEnableOpen, setTwoFactorEnableOpen] = useState(false);
  const [twoFactorDisableOpen, setTwoFactorDisableOpen] = useState(false);
  const [recoveryCodesRemaining, setRecoveryCodesRemaining] = useState<number | null>(null);

  const twoFactorEnabled = !!currentUser?.twoFactorEnabled;

  useEffect(() => {
    // Nothing to fetch while 2FA is off — the row's copy only reads
    // recoveryCodesRemaining inside the `twoFactorEnabled` branch below, so a
    // stale count left over from a prior enabled state is never shown.
    if (!twoFactorEnabled) return;
    let cancelled = false;
    void authService.getTwoFactorStatus().then(
      (s) => {
        if (!cancelled) setRecoveryCodesRemaining(s.recoveryCodesRemaining);
      },
      () => {
        if (!cancelled) setRecoveryCodesRemaining(null);
      },
    );
    return () => {
      cancelled = true;
    };
  }, [twoFactorEnabled]);

  if (!currentUser) return null;

  async function save(fields: Record<string, unknown>, message: string) {
    await updateProfile(fields as Parameters<typeof updateProfile>[0]);
    toast(message);
  }

  /**
   * 1.24.00 — the two DM switches change what the SERVER puts in the
   * conversation payload (`otherReadAt`, `online`), not just what this page
   * renders. Nothing else on this screen refetches conversations, and the 10s
   * poll is skipped whenever the realtime socket is up, so without this a
   * "Seen" line or an online dot the member just switched off stays on screen
   * until they happen to reload. Reuses the one conversation-refresh path.
   */
  async function saveAndRefreshConversations(fields: Record<string, unknown>, message: string) {
    await save(fields, message);
    await refreshConversations();
  }

  function handlePrivateToggle(next: boolean) {
    // Flipping OFF→ON with "keep existing followers" off demotes every
    // current follower to a pending request — confirm before that happens,
    // rather than after (the backend has already committed it by the time
    // the response comes back).
    if (next && currentUser?.keepFollowersOnPrivate === false) {
      setConfirmDropFollowers(true);
      return;
    }
    void save(
      { privateAccount: next },
      next ? 'Your posts are now visible to followers only' : 'Your posts are public again',
    );
  }

  async function handleSignoutOthers() {
    setClearing(true);
    try {
      const cleared = await signOutOtherSessions();
      toast(
        cleared === 0
          ? 'No other sessions were active'
          : `Signed out ${cleared} other ${cleared === 1 ? 'session' : 'sessions'}`,
      );
    } catch {
      toast('Could not sign out other sessions. Please try again');
    } finally {
      setClearing(false);
    }
  }

  return (
    <Stack>
      <Card>
        <CardTitle>Your content</CardTitle>
        <Row>
          <RowText>
            <RowLabel>Private account</RowLabel>
            <RowHint>
              Only your followers see your posts. Unlike other platforms, your comments on public
              posts stay visible. Going private hides your posts, not your voice.
            </RowHint>
          </RowText>
          <Switch
            checked={!!currentUser.privateAccount}
            onChange={handlePrivateToggle}
            label="Private account"
          />
        </Row>
        {/* 1.27.60 (owner ruling): videos get their own layer between the account and
            each video — see backend apps/content/visibility.py, gate 4. */}
        <Row>
          <RowText>
            <RowLabel>Your channel</RowLabel>
            <RowHint>
              Who sees your videos when a video keeps its default. Each video can still choose its
              own audience, including Unlisted (only people with the link).
            </RowHint>
          </RowText>
          <Select
            aria-label="Your channel"
            value={currentUser.videoChannel ?? 'inherit'}
            onChange={(e) => {
              const next = e.target.value as VideoChannel;
              void save({ videoChannel: next }, CHANNEL_TOAST[next]);
            }}
          >
            <option value="inherit">
              Same as my account ({currentUser.privateAccount ? 'followers only' : 'public'})
            </option>
            <option value="public">Public</option>
            <option value="followers">Followers only</option>
          </Select>
        </Row>
        {/* Deliberately NOT gated on currentUser.privateAccount — a public
            user must be able to pre-set this before ever flipping private,
            or the only way to reach it is the confirm dialog's own copy,
            which is too late to change anything. */}
        <Row>
          <RowText>
            <RowLabel>Keep existing followers when you switch to private</RowLabel>
            <RowHint>
              On by default. When off, going private demotes your current followers to pending
              requests they&apos;ll need to re-send.
            </RowHint>
          </RowText>
          <Switch
            checked={currentUser.keepFollowersOnPrivate !== false}
            onChange={(next) =>
              void save(
                { keepFollowersOnPrivate: next },
                next
                  ? 'Existing followers will be kept if you go private'
                  : 'Existing followers will be asked to re-follow if you go private',
              )
            }
            label="Keep existing followers when you switch to private"
          />
        </Row>
        <Row>
          <RowText>
            <RowLabel>Show the posts you cherished on your profile</RowLabel>
            <RowHint>When off, only you can see the posts you&apos;ve cherished.</RowHint>
          </RowText>
          <Switch
            checked={currentUser.likesPublic}
            onChange={(next) =>
              void save(
                { likesPublic: next },
                next ? 'Your cherished posts are now public' : 'Your cherished posts are now private',
              )
            }
            label="Show the posts you cherished on your profile"
          />
        </Row>
      </Card>

      {currentUser.privateAccount && <FollowRequestsCard />}

      {confirmDropFollowers && (
        <ConfirmDropFollowersDialog
          onClose={() => setConfirmDropFollowers(false)}
          onConfirm={() => {
            setConfirmDropFollowers(false);
            void save(
              { privateAccount: true },
              'Your posts are now visible to followers only. Existing followers must re-request',
            );
          }}
        />
      )}

      <Card>
        <CardTitle>Interactions</CardTitle>
        <Row>
          <RowText>
            <RowLabel>Who can message you</RowLabel>
            <RowHint>
              Applies to direct messages. Group conversations you join are not affected.
            </RowHint>
          </RowText>
          <AudienceSelect
            label="Who can message you"
            value={(currentUser.dmPolicy as Audience) ?? 'everyone'}
            onChange={(next) => void save({ dmPolicy: next }, 'Message settings updated')}
          />
        </Row>
        {/* 1.24.00 — both switches are RECIPROCAL and the hint has to say so.
            A one-way "stop sending receipts" toggle is the thing people expect
            and the thing that would be dishonest here: turning it off also
            turns off what you can see. */}
        <Row>
          <RowText>
            <RowLabel>Send read receipts</RowLabel>
            <RowHint>
              When off, no one sees when you&apos;ve read their message, and you stop seeing when
              they&apos;ve read yours.
            </RowHint>
          </RowText>
          <Switch
            checked={currentUser.shareReadReceipts !== false}
            onChange={(next) =>
              void saveAndRefreshConversations(
                { shareReadReceipts: next },
                next ? 'Read receipts are on' : 'Read receipts are off for both sides',
              )
            }
            label="Send read receipts"
          />
        </Row>
        <Row>
          <RowText>
            <RowLabel>Show when you&apos;re active</RowLabel>
            <RowHint>
              When off, you never show as online or typing, and you stop seeing anyone else&apos;s.
            </RowHint>
          </RowText>
          <Switch
            checked={currentUser.showActivityStatus !== false}
            onChange={(next) =>
              void saveAndRefreshConversations(
                { showActivityStatus: next },
                next ? 'Your activity status is on' : 'Your activity status is hidden both ways',
              )
            }
            label="Show when you're active"
          />
        </Row>
        <Row>
          <RowText>
            <RowLabel>Who can mention you</RowLabel>
            <RowHint>
              Mentions from anyone else won&apos;t notify you. Posts themselves are unaffected: this
              controls your notifications, not their speech.
            </RowHint>
          </RowText>
          <AudienceSelect
            label="Who can mention you"
            value={(currentUser.mentionPolicy as Audience) ?? 'everyone'}
            onChange={(next) => void save({ mentionPolicy: next }, 'Mention settings updated')}
          />
        </Row>
        <Row>
          <RowText>
            <RowLabel>Require my approval before I&apos;m added to a group chat</RowLabel>
            <RowHint>
              On by default. When off, people you already accept direct messages from can add you
              straight into groups; everyone else still needs your approval.
            </RowHint>
          </RowText>
          <Switch
            checked={currentUser.groupInvitesRequired !== false}
            onChange={(next) =>
              void save(
                { groupInvitesRequired: next },
                next
                  ? 'You now approve every group add'
                  : 'People you accept DMs from can add you directly',
              )
            }
            label="Require my approval before I'm added to a group chat"
          />
        </Row>
        <Row>
          <RowText>
            <RowLabel>Suggest your account to others</RowLabel>
            <RowHint>When off, you won&apos;t appear in anyone&apos;s Who-to-Follow.</RowHint>
          </RowText>
          <Switch
            checked={currentUser.suggestible !== false}
            onChange={(next) =>
              void save(
                { suggestible: next },
                next
                  ? 'Your account can be suggested again'
                  : 'Your account will no longer be suggested',
              )
            }
            label="Suggest your account to others"
          />
        </Row>
      </Card>

      <Card>
        <CardTitle>Session security</CardTitle>
        <Row>
          <RowText>
            <RowLabel>Alert me about new sign-ins</RowLabel>
            <RowHint>Get a notification whenever your account signs in somewhere.</RowHint>
          </RowText>
          <Switch
            checked={!!currentUser.loginAlerts}
            onChange={(next) =>
              void save(
                { loginAlerts: next },
                next ? 'Sign-in alerts are on' : 'Sign-in alerts are off',
              )
            }
            label="Alert me about new sign-ins"
          />
        </Row>
        <Row>
          <RowText>
            <RowLabel>Sign out everywhere else</RowLabel>
            <RowHint>
              Ends every other session on this account: other browsers, other devices, and the
              mobile app. This one stays signed in.
            </RowHint>
          </RowText>
          <Button
            variant="secondary"
            onClick={() => void handleSignoutOthers()}
            disabled={clearing}
          >
            {clearing ? 'Signing out…' : 'Sign out others'}
          </Button>
        </Row>
        <Row>
          <RowText>
            <RowLabel>Two-factor authentication</RowLabel>
            <RowHint>
              {twoFactorEnabled
                ? `Adds a code from your authenticator app at sign-in.${
                    recoveryCodesRemaining != null
                      ? ` ${recoveryCodesRemaining} recovery ${recoveryCodesRemaining === 1 ? 'code' : 'codes'} left.`
                      : ''
                  }`
                : 'Required for a verified badge. Adds a code from an authenticator app at sign-in.'}
            </RowHint>
          </RowText>
          <RowActions>
            <Badge tone={twoFactorEnabled ? 'cyan' : 'neutral'}>
              {twoFactorEnabled ? 'On' : 'Off'}
            </Badge>
            {twoFactorEnabled ? (
              <Button variant="secondary" onClick={() => setTwoFactorDisableOpen(true)}>
                Turn off
              </Button>
            ) : (
              <Button variant="secondary" onClick={() => setTwoFactorEnableOpen(true)}>
                Enable
              </Button>
            )}
          </RowActions>
        </Row>
      </Card>

      <TwoFactorEnableDialog
        open={twoFactorEnableOpen}
        onClose={() => setTwoFactorEnableOpen(false)}
        onEnabled={() => {
          setTwoFactorEnableOpen(false);
          toast('Two-factor authentication is on');
        }}
      />
      <TwoFactorDisableDialog
        open={twoFactorDisableOpen}
        onClose={() => setTwoFactorDisableOpen(false)}
        onDisabled={() => setTwoFactorDisableOpen(false)}
      />
    </Stack>
  );
}
